import {
  ChangeDetectionStrategy,
  Component,
  computed,
  contentChild,
  EventEmitter,
  inject,
  input,
  InputSignal,
  OnDestroy,
  OnInit,
  Output,
  Signal,
  signal,
  WritableSignal
} from '@angular/core';
import { MatMenuModule } from '@angular/material/menu';
import { IconModule } from '@ogs-gmbh/ngx-m3-components';
import { DEFAULT_SORT_DIRECTION, SortDirection } from '../types/sorting-directions';
import { TableHeaderIconResolverComponent } from './components/table-header-icon-resolver/table-header-icon-resolver.component';
import { SortDirectionChangeEvent } from '../types/events/sort-direction-change.type';
import { HEADER_DISPLAY_TYPES } from '@shared/components/table-implementation/bcl-table-data/header-display-type';
import { FaIconLibrary } from '@fortawesome/angular-fontawesome';
import { TranslationPipeModule } from '@ogs-gmbh/ngx-translate';
import { Subject, takeUntil, throttleTime } from 'rxjs';
import { faEllipsisVertical, faImage } from '@fortawesome/pro-light-svg-icons';
import { TableColumnMenuService } from './components/table-column-menu/services/table-column-menu.service';
import { TableColumnMenuComponent } from './components/table-column-menu/table-column-menu.component';
import { TableConfigService } from '@core/services/table/table-config.service';
import { TableColumn } from '../types/column/header.type';
import { FloatingDirective } from '@core/directives/floating/floating.directive';
import { TABLE_THEME, TableTheme } from '@shared/components/table/types/table-theme.type';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'app-bcl-table-header',
  standalone: true,
  imports: [
    IconModule,
    MatMenuModule,
    TableHeaderIconResolverComponent,
    TranslationPipeModule,
    FloatingDirective
  ],
  providers: [ TableColumnMenuService ],
  templateUrl: './bcl-table-header.component.html',
  styleUrl: './bcl-table-header.component.scss'
})
export class BclTableHeaderComponent implements OnInit, OnDestroy {
  private _faIconLibrary: FaIconLibrary = inject(FaIconLibrary);

  private _tableColumnMenuService: TableColumnMenuService = inject(TableColumnMenuService);

  private _theme: TableTheme = inject(TABLE_THEME);

  private _tableConfigService: TableConfigService = inject(TableConfigService);

  protected isMenuOpen: WritableSignal<boolean> = signal(false);

  private _menu: Signal<TableColumnMenuComponent | undefined> = contentChild(TableColumnMenuComponent);

  // eslint-disable-next-line @unicorn/consistent-function-scoping
  protected hasMenu: Signal<boolean> = computed(() => this._menu() !== undefined);

  public header: InputSignal<TableColumn> = input.required();

  private _hostClick$: Subject<void> = new Subject<void>();

  private _destroy$: Subject<void> = new Subject<void>();

  protected readonly HEADER_DISPLAY_TYPES: typeof HEADER_DISPLAY_TYPES = HEADER_DISPLAY_TYPES;

  @Output() readonly sortDirectionChangedEvent: EventEmitter<SortDirectionChangeEvent> = new EventEmitter<SortDirectionChangeEvent>();

  ngOnInit (): void {
    this._faIconLibrary.addIcons(faEllipsisVertical, faImage);
    this._tableColumnMenuService.provideHeader(this.header());
    this._hostClick$
      .pipe(
        throttleTime(150, undefined, { leading: true, trailing: true }),
        takeUntil(this._destroy$)
      )
      .subscribe(() => {
        const newSortDirection: SortDirection = this.getNextSortDirection();

        this._tableConfigService.changeColumnSortDirection(this.header().field, newSortDirection);
      });
  }

  public onClick (): void {
    this._hostClick$.next();
  }

  ngOnDestroy (): void {
    this._destroy$.next();
    this._destroy$.complete();
  }


  getNextSortDirection (): SortDirection {
    if (this._theme.sortDirectionOrder.length > 0) {
      const indexOfSelectedSortDirection: number = this._theme.sortDirectionOrder.indexOf(this.header().options.sortDirection);

      // eslint-disable-next-line @tseslint/no-non-null-assertion
      return indexOfSelectedSortDirection === this._theme.sortDirectionOrder.length - 1 || indexOfSelectedSortDirection === -1 ? this._theme.sortDirectionOrder[ 0 ]! : this._theme.sortDirectionOrder[ indexOfSelectedSortDirection + 1 ]!;
    }

    return DEFAULT_SORT_DIRECTION;
  }
}

